import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Layers, PanelLeftClose, PanelLeftOpen, Plus, Upload } from "lucide-react";
import API_BASE_URL, { BRAND_NAME, BRAND_TAGLINE } from "../config";
import { navGroupsFor } from "../navigation";
import { isMobileViewport } from "../utils/breakpoints";
import { useJobsContext } from "../contexts/JobsProvider";
import { isDoneStatus, isFailureStatus } from "../utils/jobStatus";
import { AGENT_LABELS as JOB_LABEL } from "../utils/agentLabels";
import useQualityMetrics from "../hooks/useQualityMetrics";
import { deriveKpis, formatPct } from "../utils/qualityMetrics";
import AccountSettings from "./AccountSettings";
import LanguageSwitcher from "./LanguageSwitcher";
import "./Sidebar.css";

const RECENT_SEARCHES_KEY = "knowledgeRecentSearches";
// Icons for the "Filter by Type" list, keyed by backend category id.
const CATEGORY_ICON = {
  alle: "📄", zulassung: "🏛", lv: "📋", katalog: "📦", eurocode: "📐", andere: "🗂",
};
function readRecentSearches() {
  try { return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || "[]"); }
  catch { return []; }
}

// Live figures from the same aggregates as the Analytics page (feature 11).
// Mounted only on the analytics tab so other tabs never trigger the requests.
export function QuickMetrics() {
  const { t } = useTranslation();
  const state = useQualityMetrics();
  const kpis = deriveKpis(state);
  const rows = [
    { label: t("sidebar.metricAccuracy"), value: formatPct(kpis.accuracy), cls: "mv-green" },
    { label: t("sidebar.metricHelpfulFeedback"), value: formatPct(kpis.helpfulRate), cls: "mv-green" },
    { label: t("sidebar.metricQueries30d"), value: kpis.queries ?? "–", cls: "" },
    { label: t("sidebar.metricReviewQueue"), value: kpis.pendingReview ?? "–", cls: "mv-rose" },
  ];
  return rows.map((m) => (
    <div key={m.label} className="metric-row">
      <span className="metric-label">{m.label}</span>
      <span className={`metric-val ${m.cls}`}>{m.value}</span>
    </div>
  ));
}

function ConversationList({
  conversations,
  activeThreadId,
  onSelect,
  onRename,
  onDelete,
  hasMore,
  onLoadMore,
}) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const startEdit = (e, conv) => {
    e.stopPropagation();
    setDeleteConfirmId(null);
    setEditingId(conv.thread_id);
    setEditTitle(conv.title || "");
  };
  const commitEdit = (thread_id) => {
    if (editTitle.trim()) onRename(thread_id, editTitle.trim());
    setEditingId(null);
  };
  const handleKeyDown = (e, thread_id) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(thread_id);
    }
    if (e.key === "Escape") setEditingId(null);
  };
  const handleDeleteClick = (e, thread_id) => {
    e.stopPropagation();
    setEditingId(null);
    setDeleteConfirmId(thread_id);
  };
  const confirmDelete = (e, thread_id) => {
    e.stopPropagation();
    setDeleteConfirmId(null);
    onDelete(thread_id);
  };
  const cancelDelete = (e) => {
    e.stopPropagation();
    setDeleteConfirmId(null);
  };

  return (
    <div className="conv-list">
      {conversations.map((conv) => (
        <div
          key={conv.thread_id}
          className={`conv-item${conv.thread_id === activeThreadId ? " active" : ""}`}
          onClick={() =>
            editingId !== conv.thread_id && onSelect(conv.thread_id)
          }
        >
          {editingId === conv.thread_id ? (
            <input
              className="conv-rename-input"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={() => commitEdit(conv.thread_id)}
              onKeyDown={(e) => handleKeyDown(e, conv.thread_id)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : deleteConfirmId === conv.thread_id ? (
            <div
              className="conv-delete-confirm"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="conv-delete-label">{t("sidebar.deleteConfirm")}</span>
              <button
                className="conv-action-btn conv-delete-yes"
                onClick={(e) => confirmDelete(e, conv.thread_id)}
              >
                {t("sidebar.yes")}
              </button>
              <button className="conv-action-btn" onClick={cancelDelete}>
                {t("sidebar.no")}
              </button>
            </div>
          ) : (
            <>
              <span className="conv-title" title={conv.title || t("sidebar.untitled")}>
                {conv.title || t("sidebar.untitled")}
              </span>
              <div className="conv-actions">
                <button
                  className="conv-action-btn"
                  onClick={(e) => startEdit(e, conv)}
                  title={t("sidebar.rename")}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  className="conv-action-btn conv-delete-btn"
                  onClick={(e) => handleDeleteClick(e, conv.thread_id)}
                  title={t("sidebar.delete")}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      {conversations.length === 0 && (
        <div className="conv-empty">{t("sidebar.noConversationsYet")}</div>
      )}
      {hasMore && (
        <button type="button" className="conv-load-more" onClick={onLoadMore}>
          {t("sidebar.loadMoreConversations")}
        </button>
      )}
    </div>
  );
}

function NavUserProfile({ user, onSignOut }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const ref = useRef(null);

  // SSO accounts carry the IdP's display name instead of a local `name`.
  const displayName = user?.name || user?.display_name;
  const initials = displayName
    ? displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="nav-user-wrapper" ref={ref}>
      <div className="nav-user-av" onClick={() => setOpen((v) => !v)}>
        {user?.avatar ? (
          <img src={user.avatar} alt={user?.name || t("sidebar.user")} />
        ) : (
          initials
        )}
      </div>

      {open && (
        <div className="user-flyout">
          <div className="user-flyout-name">{displayName || user?.email || t("sidebar.user")}</div>
          <div className="user-flyout-label">{t("sidebar.signedIn")}</div>
          <div className="user-flyout-divider" />
          <LanguageSwitcher className="user-flyout-lang-switcher" />
          <button
            className="user-flyout-signout"
            onClick={() => {
              setOpen(false);
              setShowAccountSettings(true);
            }}
          >
            {t("sidebar.accountSettings")}
          </button>
          <button
            className="user-flyout-signout"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            {t("sidebar.signOut")}
          </button>
        </div>
      )}

      {showAccountSettings && (
        <AccountSettings user={user} onClose={() => setShowAccountSettings(false)} />
      )}
    </div>
  );
}

export default function Sidebar({
  activeTab,
  setActiveTab,
  sidebarOpen,
  toggleSidebar,
  conversations,
  hasMoreConversations,
  onLoadMoreConversations,
  threadId,
  onNewChat,
  onSelectConversation,
  onRenameConversation,
  onDeleteConversation,
  user,
  onSignOut,
  selectedDb,
  onDbChange,
  runsRefreshKey,
  onOpenRun,
  onOpenWorkflow,
  searchCategory,
  onSearchCategoryChange,
  onSelectRecentSearch,
}) {
  const { t } = useTranslation();
  const [recentRuns, setRecentRuns] = useState([]);
  const [recentRunsError, setRecentRunsError] = useState(false);
  const [confirmRunId, setConfirmRunId] = useState(null);
  const [searchCategories, setSearchCategories] = useState([]);
  const recentSearches = readRecentSearches();
  const activeCategory = searchCategory || "alle";

  // Document-type categories for the "Filter by Type" list — same source as the
  // search-page chips, so labels/counts stay consistent. Degrades silently.
  useEffect(() => {
    if (activeTab !== "search") return;
    let cancelled = false;
    const qs = selectedDb ? `?database=${encodeURIComponent(selectedDb)}` : "";
    fetch(`${API_BASE_URL}/api/v1/knowledge/categories${qs}`, {
      headers: { "ngrok-skip-browser-warning": "69420" },
    })
      .then((r) => (r.ok ? r.json() : { categories: [] }))
      .then((d) => { if (!cancelled) setSearchCategories(Array.isArray(d.categories) ? d.categories : []); })
      .catch(() => { if (!cancelled) setSearchCategories([]); });
    return () => { cancelled = true; };
  }, [activeTab, selectedDb]);

  // Shared with ActivityMenu via JobsProvider (one poller, not two). Used here only
  // to keep Recent Runs fresh when a job auto-saves — active jobs are shown in the
  // global top-right ActivityMenu, not here.
  const { jobs } = useJobsContext();

  const loadRecentRuns = useCallback(() => {
    fetch(`${API_BASE_URL}/api/v1/runs`)
      .then((r) => {
        if (!r.ok) throw new Error(`runs fetch failed: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setRecentRuns(Array.isArray(data) ? data : []);
        setRecentRunsError(false);
      })
      .catch(() => setRecentRunsError(true));
  }, []);

  // Delete a saved run from the permanent history (optimistic removal).
  const deleteRun = useCallback((id) => {
    setConfirmRunId(null);
    setRecentRuns((prev) => prev.filter((r) => r.id !== id));
    fetch(`${API_BASE_URL}/api/v1/runs/${id}`, { method: "DELETE" }).catch(() => loadRecentRuns());
  }, [loadRecentRuns]);

  // Recent Runs section (defined once so it can sit at the TOP of the workflow tab).
  const recentRunsSection = (
    <>
      <span className="ctx-section-label">{t("sidebar.recentRuns")}</span>
      {recentRunsError ? (
        <div className="ctx-item dim">{t("sidebar.runHistoryUnavailable")}</div>
      ) : recentRuns.length === 0 ? (
        <div className="ctx-item dim">{t("sidebar.noRunsYet")}</div>
      ) : (
        recentRuns.map((run) => (
          <div
            key={run.id}
            className="ctx-item ctx-item--clickable run-item"
            title={`${run.project_name} — open saved run`}
            onClick={() => confirmRunId !== run.id && onOpenRun?.(run.id)}
          >
            <span className={`ctx-item-icon ${run.verdict ? "ctx-ok" : "ctx-fail"}`}>
              {run.verdict ? "✓" : "✗"}
            </span>
            <span className="ctx-item-text">
              {(JOB_LABEL[run.agent_type] || run.workflow_id)} · {run.project_name}
            </span>
            {confirmRunId === run.id ? (
              <span className="run-confirm" onClick={(e) => e.stopPropagation()}>
                <button className="run-confirm-yes"
                  onClick={(e) => { e.stopPropagation(); deleteRun(run.id); }}>{t("sidebar.delete")}</button>
                <button className="run-confirm-no"
                  onClick={(e) => { e.stopPropagation(); setConfirmRunId(null); }}>{t("sidebar.cancel")}</button>
              </span>
            ) : (
              <button className="run-del-btn" title={t("sidebar.removeFromHistory")}
                onClick={(e) => { e.stopPropagation(); setConfirmRunId(run.id); }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
              </button>
            )}
          </div>
        ))
      )}
    </>
  );

  // Load the persisted Recent Runs whenever a run is approved (runsRefreshKey bumps)
  // or the workflow tab is opened.
  useEffect(() => {
    if (activeTab !== "workflow") return;
    loadRecentRuns();
  }, [activeTab, runsRefreshKey, loadRecentRuns]);

  // Completed background jobs auto-save to Recent Runs server-side, so refetch the
  // list when the number of finished jobs grows — otherwise the new run wouldn't
  // appear until a manual approve or tab re-open.
  const prevDoneRef = useRef(0);
  useEffect(() => {
    const done = jobs.filter((j) => isDoneStatus(j.status) || isFailureStatus(j.status)).length;
    if (done > prevDoneRef.current && activeTab === "workflow") loadRecentRuns();
    prevDoneRef.current = done;
  }, [jobs, activeTab, loadRecentRuns]);

  // Nav selects the tab; on mobile the drawer closes after choosing.
  const handleNavClick = (id) => {
    setActiveTab(id);
    if (sidebarOpen && isMobileViewport()) toggleSidebar();
  };

  const groups = navGroupsFor(user?.role);
  const contextAction =
    activeTab === "chat" ? { label: t("sidebar.newChat"), Icon: Plus, onClick: onNewChat }
    : activeTab === "kb" ? { label: t("sidebar.uploadDocument"), Icon: Upload, onClick: undefined }
    : null;

  return (
    <aside className={`sb ${sidebarOpen ? "sb--open" : "sb--collapsed"}`}>
      <div className="sb-brand">
        <div className="sb-mark" aria-hidden="true"><Layers size={18} /></div>
        <div className="sb-brand-text">
          <div className="sb-brand-name">{BRAND_NAME}</div>
          <div className="ds-eyebrow sb-brand-tagline">{BRAND_TAGLINE}</div>
        </div>
        <button
          type="button"
          className="sb-collapse"
          aria-label={t(sidebarOpen ? "sidebar.collapse" : "sidebar.expand")}
          onClick={toggleSidebar}
        >
          {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>
      </div>

      <div className="sb-workspace">
        <span className="ds-live-dot" aria-hidden="true" />
        <select
          className="sb-ws-select"
          aria-label={t("sidebar.workspace")}
          value={selectedDb}
          onChange={(e) => onDbChange(e.target.value)}
        >
          <option value="Lucking">Lücking</option>
          <option value="Betonbau">Betonbau</option>
          <option value="Tchalabi">Tchalabi</option>
          <option value="Baseline">Baseline</option>
          <option value="ArabicLegal">Arabic Legal</option>
        </select>
      </div>

      <nav className="sb-nav">
        {groups.map((group) => (
          <div key={group.id} className="sb-group">
            <div className="ds-eyebrow sb-group-label">{t(group.labelKey)}</div>
            {group.items.map(({ id, labelKey, Icon }) => (
              <button
                key={id}
                type="button"
                className={`sb-item${activeTab === id ? " sb-item--active" : ""}`}
                aria-label={t(labelKey)}
                aria-current={activeTab === id ? "page" : undefined}
                title={sidebarOpen ? undefined : t(labelKey)}
                onClick={() => handleNavClick(id)}
              >
                <Icon size={16} aria-hidden="true" />
                <span className="sb-item-label">{t(labelKey)}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className="sb-context">
        {contextAction && (
          <button type="button" className="sb-context-action" onClick={contextAction.onClick}>
            <contextAction.Icon size={14} aria-hidden="true" />
            {contextAction.label}
          </button>
        )}
        <div className="ctx-body">

            {activeTab === "chat" && (
              <>
                <span className="ctx-section-label">{t("sidebar.recent")}</span>
                <ConversationList
                  conversations={conversations}
                  activeThreadId={threadId}
                  onSelect={onSelectConversation}
                  onRename={onRenameConversation}
                  onDelete={onDeleteConversation}
                  hasMore={hasMoreConversations}
                  onLoadMore={onLoadMoreConversations}
                />
              </>
            )}

            {activeTab === "search" && (
              <>
                <span className="ctx-section-label">{t("sidebar.filterByType")}</span>
                {searchCategories.map((c) => (
                  <div
                    key={c.id}
                    className={`ctx-item ctx-item--clickable${activeCategory === c.id ? " active" : ""}`}
                    title={t("sidebar.filterByTypeTooltip", { label: c.label })}
                    onClick={() => onSearchCategoryChange?.(c.id)}
                  >
                    <span className="ctx-item-icon">{CATEGORY_ICON[c.id] || "📄"}</span>
                    <span className="ctx-item-text">{c.label}</span>
                    {typeof c.count === "number" && (
                      <span className="ctx-item-count">{c.count}</span>
                    )}
                  </div>
                ))}
                <span className="ctx-section-label">{t("sidebar.recentSearches")}</span>
                {recentSearches.length === 0 ? (
                  <div className="ctx-item dim">{t("sidebar.noRecentSearches")}</div>
                ) : (
                  recentSearches.map((s) => (
                    <div
                      key={s}
                      className="ctx-item ctx-item--clickable"
                      title={t("sidebar.rerunSearchTooltip", { query: s })}
                      onClick={() => onSelectRecentSearch?.(s)}
                    >
                      <span className="ctx-item-icon">🕐</span>
                      <span className="ctx-item-text">{s}</span>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === "workflow" && (
              <>
                <span className="ctx-section-label">{t("sidebar.startWorkflows")}</span>
                {[
                  { id: "WF-01", title: t("sidebar.approvalAnalysis") },
                  { id: "WF-02", title: t("sidebar.lvMatching") },
                  { id: "WF-03", title: t("sidebar.planTakeoff") },
                ].map((wf) => (
                  <div
                    key={wf.id}
                    className={`wf-ctx-item${wf.dim ? " dim" : " ctx-item--clickable"}`}
                    title={wf.dim ? t("sidebar.inDevelopment", { title: wf.title }) : t("sidebar.openWorkflow", { title: wf.title })}
                    onClick={() => { if (!wf.dim) onOpenWorkflow?.(wf.id); }}
                  >
                    <div className="wf-ctx-num">{wf.id}</div>
                    <div className="wf-ctx-title">{wf.title}</div>
                    <div className="wf-ctx-status">{wf.dim ? t("sidebar.dev") : t("sidebar.open")}</div>
                  </div>
                ))}
                {recentRunsSection}
              </>
            )}

            {activeTab === "kb" && (
              <>
                <span className="ctx-section-label">{t("sidebar.documentsCount", { count: 47 })}</span>
                {[
                  {
                    badge: "Z17",
                    cls: "cb-z17",
                    name: "Z-17.21-1227",
                    meta: "13p · Jan 2024 · 126 nodes",
                  },
                  {
                    badge: "Z17",
                    cls: "cb-z17",
                    name: "Z-17.1-1149",
                    meta: "8p · Feb 2025 · 94 nodes",
                  },
                  {
                    badge: "LV",
                    cls: "cb-lv",
                    name: "LV_Rohbau_Spieck_BA2",
                    meta: "47p · 312 positions",
                  },
                  {
                    badge: "CAT",
                    cls: "cb-pdf",
                    name: "Produkthandbuch 2025",
                    meta: "98p · 847 products",
                  },
                ].map((doc) => (
                  <div key={doc.name} className="ctx-doc">
                    <div className="ctx-doc-name">
                      <span className={`ctx-badge ${doc.cls}`}>
                        {doc.badge}
                      </span>
                      {doc.name}
                    </div>
                    <div className="ctx-doc-meta">{doc.meta}</div>
                  </div>
                ))}
              </>
            )}

            {activeTab === "analytics" && (
              <>
                <span className="ctx-section-label">{t("sidebar.quickMetrics")}</span>
                <QuickMetrics />
              </>
            )}
        </div>
      </div>

      <div className="sb-user">
        <NavUserProfile user={user} onSignOut={onSignOut} />
        <div className="acc-ring">80%</div>
      </div>
    </aside>
  );
}
